import { PublicClientApplication, SilentRequest, PopupRequest } from '@azure/msal-browser';
import devUsersConfig from '../config/devUsers.json';

// Detect whether we're running in a browser environment with Web Crypto available
const isBrowser = typeof window !== 'undefined' && typeof window.crypto !== 'undefined' && !!(window.crypto as any).subtle;

// Dev user types and state
export interface DevUser {
  id: string;
  displayName: string;
  email: string;
  isDefault?: boolean;
}

// Check if we're in local development mode (includes GitHub Codespaces)
export const isLocalDevelopment = (): boolean => {
  return isBrowser && (
    window.location.hostname === 'localhost' ||
    window.location.hostname.startsWith('127.0.0.1') ||
    window.location.hostname.endsWith('.app.github.dev')
  );
};

// Session storage key for selected dev user
const DEV_USER_STORAGE_KEY = 'devSelectedUserId';

// Anonymous sentinel value — represents an unauthenticated local dev session
export const ANONYMOUS_USER_ID = 'anonymous';

// Initialize selected dev user from sessionStorage (persists across page reloads)
const getStoredDevUserId = (): string | null => {
  if (!isBrowser) return null;
  try {
    return sessionStorage.getItem(DEV_USER_STORAGE_KEY);
  } catch {
    return null;
  }
};

// Session state for selected dev user (initialized from sessionStorage)
let selectedDevUserId: string | null = getStoredDevUserId();

// Get all available dev users from config
export const getDevUsers = (): DevUser[] => {
  return devUsersConfig.users;
};

// Check if the current dev session is anonymous (unauthenticated)
export const isAnonymousDevUser = (): boolean => {
  // If no user has been explicitly selected yet, treat as anonymous
  if (!selectedDevUserId) return true;
  return selectedDevUserId === ANONYMOUS_USER_ID;
};

// Get the currently selected dev user (or default)
export const getSelectedDevUser = (): DevUser => {
  const users = getDevUsers();
  if (selectedDevUserId && selectedDevUserId !== ANONYMOUS_USER_ID) {
    const found = users.find(u => u.id === selectedDevUserId);
    if (found) return found;
  }
  // Return default user or first user
  return users.find(u => u.isDefault) || users[0];
};

// Get the default dev user (used when signing in from anonymous)
export const getDefaultDevUser = (): DevUser => {
  const users = getDevUsers();
  return users.find(u => u.isDefault) || users[0];
};

// Set the current dev user (for user switching)
export const setDevUser = (userId: string): void => {
  // Allow switching to anonymous (unauthenticated) mode
  if (userId === ANONYMOUS_USER_ID) {
    selectedDevUserId = ANONYMOUS_USER_ID;
    if (isBrowser) {
      try {
        sessionStorage.setItem(DEV_USER_STORAGE_KEY, ANONYMOUS_USER_ID);
      } catch {
        // Ignore storage errors
      }
    }
    return;
  }

  const users = getDevUsers();
  const found = users.find(u => u.id === userId);
  if (found) {
    selectedDevUserId = userId;
    // Persist to sessionStorage so it survives page reloads
    if (isBrowser) {
      try {
        sessionStorage.setItem(DEV_USER_STORAGE_KEY, userId);
      } catch {
        // Ignore storage errors
      }
    }
  }
};

// Subscribe to dev user changes
type DevUserChangeCallback = (user: DevUser) => void;
const devUserChangeCallbacks: DevUserChangeCallback[] = [];

export const subscribeToDevUserChange = (callback: DevUserChangeCallback): (() => void) => {
  devUserChangeCallbacks.push(callback);
  return () => {
    const index = devUserChangeCallbacks.indexOf(callback);
    if (index > -1) {
      devUserChangeCallbacks.splice(index, 1);
    }
  };
};

// Notify subscribers when dev user changes
export const switchDevUser = (userId: string): void => {
  setDevUser(userId);
  const user = getSelectedDevUser();
  devUserChangeCallbacks.forEach(cb => cb(user));
};

// Extract the base domain regardless of current path (fallback for tests/node)
const baseUrl = isBrowser ? window.location.origin : 'http://localhost';

// MSAL configuration for popup-based authentication with public client flow
const msalConfig = {
  auth: {
    clientId: 'c81b0d57-be15-452f-af8d-6899ebe42906',
    authority: 'https://login.microsoftonline.com/9188040d-6c67-4c5b-b112-36a304b66dad',
    redirectUri: `${baseUrl}`,
    navigateToLoginRequestUrl: false
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: true
  }
};

// Create MSAL instance only when running in a real browser; otherwise provide a safe stub
function createMsalStub() {
  return {
    initialize: async () => {},
    getAllAccounts: () => [] as any[],
    loginPopup: async (_: any) => { throw new Error('MSAL not available in test environment'); },
    acquireTokenSilent: async (_: any) => { throw new Error('MSAL not available in test environment'); },
    acquireTokenPopup: async (_: any) => { throw new Error('MSAL not available in test environment'); }
  } as unknown as PublicClientApplication;
}

const msalInstance: PublicClientApplication = isBrowser
  ? new PublicClientApplication(msalConfig)
  : createMsalStub();

// Initialize MSAL
let initializePromise: Promise<void>;

const initializeMsal = async (): Promise<void> => {
  if (!initializePromise) {
    initializePromise = msalInstance.initialize().then(() => {
      // Initialization complete
    });
  }
  return initializePromise;
};

// Initialize immediately to speed up subsequent calls
initializeMsal().catch(error => {
  console.error("Failed to initialize MSAL:", error);
  // We can't use the error context here since it's outside of a React component
});

// Use default login scopes for initial login
const loginScopes = ["openid", "profile", "email"];

// Use openid scopes to acquire ID tokens for our own API
const apiScope = ["openid", "profile", "email"];

// Function to check if a user is logged in
export const isUserLoggedIn = async (): Promise<boolean> => {
  // For local development, check if a real dev user is selected (not anonymous)
  if (isLocalDevelopment()) {
    return !isAnonymousDevUser();
  }

  await initializeMsal();
  const accounts = msalInstance.getAllAccounts();
  return accounts.length > 0;
};

// Error handling helper function to be used in service methods
// This can be used directly in components that have access to the ErrorContext
let globalErrorHandler: ((message: string) => void) | null = null;

export const setErrorHandler = (handler: (message: string) => void) => {
  globalErrorHandler = handler;
};

// Function to handle errors in a consistent way
const handleError = (message: string, error: unknown) => {
  const errorMsg = error instanceof Error ? `${message}: ${error.message}` : message;
  console.error(errorMsg, error);
  
  // If we have a global error handler, use it to show the error modal
  if (globalErrorHandler) {
    globalErrorHandler(errorMsg);
  }
  
  return null;
};


// Function to handle login with popup (user stays on same page)
export const loginUser = async (): Promise<boolean> => {
  try {
    await initializeMsal();
    const loginRequest = {
      scopes: loginScopes, // Use basic login scopes for authentication
    };
    
    await msalInstance.loginPopup(loginRequest);
    return true;
  } catch (error) {
    handleError('Login failed', error);
    return false;
  }
};

// Function to handle logout with popup (clears Azure AD session)
export const logoutUser = async (): Promise<void> => {
  await initializeMsal();
  await msalInstance.logoutPopup();
};

// Function to get the current user ID
export const getCurrentUserId = async (): Promise<string | null> => {
  // For local development, return selected dev user ID (null if anonymous)
  if (isLocalDevelopment()) {
    if (isAnonymousDevUser()) return null;
    return getSelectedDevUser().id;
  }

  try {
    await initializeMsal();

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      const account = accounts[0];

      // Try to get the user ID from ID token claims first (should match backend's "sub")
      if (account.idTokenClaims) {
        const userId = account.idTokenClaims['sub'] || account.idTokenClaims['oid'];
        if (userId) {
          return userId;
        }
      }

      // If no claims available, try to acquire ID token to get claims
      try {
        const request = {
          scopes: apiScope,
          account: account
        };

        const response = await msalInstance.acquireTokenSilent(request);

        if (response.idTokenClaims) {
          const claims = response.idTokenClaims as any;
          const userId = claims['sub'] || claims['oid'];
          if (userId) {
            return userId;
          }
        }
      } catch (error) {
        // Silent token acquisition failed, continue to fallback
      }

      // Fallback to MSAL account IDs
      return account.localAccountId || account.homeAccountId;
    }

    return null;
  } catch (error) {
    console.error('Error getting current user ID:', error);
    return null;
  }
};

// Scopes needed for Microsoft Graph API (OneDrive file access)
const graphScopes = ["Files.Read"];

// Scopes needed for OneDrive Consumer picker (maps to Files.Read app permission)
const oneDrivePickerScopes = ["OneDrive.ReadOnly"];

// Function to get an access token for Microsoft Graph API (file downloads).
// Unlike getAuthToken() which returns ID tokens for our own API,
// this always uses real MSAL — even in local dev — because OneDrive
// requires a real Microsoft identity regardless of environment.
export const getGraphAccessToken = async (): Promise<string> => {
  return acquireAccessToken(graphScopes);
};

// Function to get an access token for the OneDrive File Picker.
// Consumer OneDrive requires the OneDrive.ReadOnly scope (not Files.Read).
export const getOneDrivePickerToken = async (): Promise<string> => {
  return acquireAccessToken(oneDrivePickerScopes);
};

async function acquireAccessToken(scopes: string[]): Promise<string> {
  await initializeMsal();

  const accounts = msalInstance.getAllAccounts();

  if (accounts.length > 0) {
    const account = accounts[0];

    try {
      const request: SilentRequest = {
        scopes,
        account
      };
      const response = await msalInstance.acquireTokenSilent(request);
      return response.accessToken;
    } catch {
      // Silent failed — fall through to interactive
    }

    const popupRequest: PopupRequest = {
      scopes,
      account
    };
    const response = await msalInstance.acquireTokenPopup(popupRequest);
    return response.accessToken;
  }

  // No accounts — go straight to interactive login (covers local dev)
  const popupRequest: PopupRequest = {
    scopes
  };
  const response = await msalInstance.acquireTokenPopup(popupRequest);
  return response.accessToken;
}

// Function to get the authentication token
export const getAuthToken = async (): Promise<string | null> => {
  // For local development, return fake token with user ID (null if anonymous)
  if (isLocalDevelopment()) {
    if (isAnonymousDevUser()) {
      console.log('Local development: anonymous user, no token');
      return null;
    }
    const userId = getSelectedDevUser().id;
    console.log(`Local development: returning fake token for user ${userId}`);
    return `fake-token:${userId}`;
  }

  try {
    await initializeMsal();

    const accounts = msalInstance.getAllAccounts();

    if (accounts.length === 0) {
      console.log('No authenticated accounts found, attempting login');
      const loginSuccess = await loginUser();
      if (!loginSuccess) {
        return handleError('Authentication failed', new Error('Could not log in'));
      }
    }

    // Try again after potential login
    const accounts2 = msalInstance.getAllAccounts();
    if (accounts2.length > 0) {
      const account = accounts2[0];

      // Prefer the cached ID token
      if (!!account.idToken) {
        return account.idToken;
      }

      // Refresh ID token silently using openid scopes
      const request: SilentRequest = {
        scopes: apiScope,
        account: account
      };

      try {
        const response = await msalInstance.acquireTokenSilent(request);
        return response.idToken;
      } catch (error) {
        console.error('Silent token acquisition failed, trying interactive method');

        try {
          const popupRequest: PopupRequest = {
            scopes: apiScope,
            account: account
          };
          const interactiveResponse = await msalInstance.acquireTokenPopup(popupRequest);
          return interactiveResponse.idToken;
        } catch (interactiveError) {
          return handleError('Interactive token acquisition failed', interactiveError);
        }
      }
    }

    return handleError('Authentication error', new Error('No authenticated accounts found after login attempt'));
  } catch (error) {
    return handleError('Error getting authentication token', error);
  }
};
